"use client";

import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { RENTAL_VEHICLES } from "@/data/vehicles";
import type { Locale } from "@/config/i18n";
import VehicleCard from "./VehicleCard";
import Link from "next/link";
import { LOCATION_BOOKING_FORM_ID } from "./locationBooking";
import { VEHICLES_UI } from "./vehiclesUi";
import { tr } from "@/config/localeText";

type SortOption =
  | "date-desc"
  | "date-asc"
  | "price-desc"
  | "price-asc"
  | "popularity";

type Brand = {
  name: string;
  count: number;
  checked: boolean;
};

const calculateBrands = () => {
  const brandCounts = RENTAL_VEHICLES.reduce(
    (acc, vehicle) => {
      const brand = vehicle.name.includes("BRABUS")
        ? "Mercedes-Brabus"
        : vehicle.brand;
      acc[brand] = (acc[brand] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return Object.entries(brandCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .map(([name, count]) => ({
      name,
      count,
      checked: false,
    }));
};

const INITIAL_BRANDS: Brand[] = calculateBrands();
const PRICE_MIN = Math.min(...RENTAL_VEHICLES.map((v) => v.price.amount));
const PRICE_MAX = Math.max(...RENTAL_VEHICLES.map((v) => v.price.amount));
const PRICE_SPAN = Math.max(PRICE_MAX - PRICE_MIN, 1);

export default function CatalogueSection({
  locale = "fr",
}: {
  locale?: Locale;
}) {
  const ui = tr(VEHICLES_UI, locale).catalogue;
  const SORT_OPTIONS = ui.sortOptions;
  const [brands, setBrands] = useState<Brand[]>(INITIAL_BRANDS);
  const [priceRange, setPriceRange] = useState<[number, number]>([
    PRICE_MIN,
    PRICE_MAX,
  ]);
  const [sortBy, setSortBy] = useState<SortOption>("date-desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [gridSize, setGridSize] = useState<2 | 3>(2);

  const filteredVehicles = RENTAL_VEHICLES.filter((vehicle) => {
    const brandChecked =
      brands.some((brand) => {
        const vehicleBrand = vehicle.name.includes("BRABUS")
          ? "Mercedes-Brabus"
          : vehicle.brand;
        return brand.name === vehicleBrand && brand.checked;
      }) || !brands.some((brand) => brand.checked);
    const priceInRange =
      vehicle.price.amount >= priceRange[0] &&
      vehicle.price.amount <= priceRange[1];
    const q = searchQuery.trim().toLowerCase();
    const matchesQuery =
      !q ||
      vehicle.name.toLowerCase().includes(q) ||
      vehicle.brand.toLowerCase().includes(q) ||
      vehicle.specs.engine.toLowerCase().includes(q);

    return brandChecked && priceInRange && matchesQuery;
  });

  const sortedVehicles = [...filteredVehicles].sort((a, b) => {
    switch (sortBy) {
      case "price-desc":
        return b.price.amount - a.price.amount;
      case "price-asc":
        return a.price.amount - b.price.amount;
      case "date-desc":
        return (
          b.specs.year - a.specs.year ||
          Number(b.isNew) - Number(a.isNew) ||
          b.price.amount - a.price.amount
        );
      case "date-asc":
        return (
          a.specs.year - b.specs.year ||
          Number(a.isNew) - Number(b.isNew) ||
          a.price.amount - b.price.amount
        );
      case "popularity":
        return b.images.gallery.length - a.images.gallery.length;
      default:
        return 0;
    }
  });

  const handleBrandToggle = (brandName: string) => {
    setBrands(
      brands.map((brand) =>
        brand.name === brandName
          ? { ...brand, checked: !brand.checked }
          : brand,
      ),
    );
  };

  const handlePriceChange = (value: number, isMin: boolean) => {
    setPriceRange((prev) => {
      if (isMin) return [Math.min(value, prev[1] - 50), prev[1]];
      return [prev[0], Math.max(value, prev[0] + 50)];
    });
  };

  return (
    <section
      id="catalogue-location"
      className="scroll-mt-28 bg-white py-14 md:scroll-mt-32 md:py-16"
    >
      <div className="mx-auto max-w-[90rem] px-6 py-6 md:px-8">
        <Link
          href={ui.backHref}
          className="mb-10 inline-block text-sm text-primaryLoc/70 underline underline-offset-4 transition-colors hover:text-primaryLoc"
        >
          {ui.back}
        </Link>

        <div className="text-center">
          <h2 className="text-3xl font-light uppercase tracking-wide text-primaryLoc md:text-4xl">
            {ui.title}
          </h2>
          <p className="mt-2 text-base text-primaryLoc/80 md:text-lg">
            {ui.intro}
          </p>
        </div>

        <div className="mt-10 grid items-start gap-8 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <div
            className={`${
              isFilterOpen
                ? "fixed inset-y-0 left-0 z-40 w-[90vw] max-w-sm transform overflow-y-auto rounded-e-2xl border-e border-primaryLoc/10 bg-white p-6 py-20 shadow-2xl transition-transform duration-300 ease-in-out"
                : "hidden lg:sticky lg:top-[120px] lg:block lg:self-start"
            }`}
          >
            {isFilterOpen && (
              <div className="mb-6 flex flex-wrap items-center justify-between border-b border-primaryLoc/10 pb-4">
                <h3 className="text-lg font-semibold text-primaryLoc">
                  {ui.filters}
                </h3>
                <button
                  title={ui.closeFilter}
                  onClick={() => setIsFilterOpen(false)}
                  className="rounded-full p-1 text-primaryLoc/70 hover:bg-secondary"
                >
                  <Icon icon="material-symbols:close" className="h-6 w-6" />
                </button>
              </div>
            )}

            <div className="space-y-5">
              <div className="rounded-2xl border border-primaryLoc/10 bg-white p-4">
                <h3 className="mb-4 font-semibold text-primaryLoc">
                  {ui.brands}
                </h3>
                <div className="space-y-3">
                  {brands.map((brand) => (
                    <label
                      key={brand.name}
                      className="flex cursor-pointer items-center gap-3"
                    >
                      <input
                        type="checkbox"
                        checked={brand.checked}
                        onChange={() => handleBrandToggle(brand.name)}
                        className="h-4 w-4 cursor-pointer rounded border border-primaryLoc/35 bg-white accent-primaryLoc focus:ring-2 focus:ring-primaryLoc/20"
                      />
                      <span className="flex-1 text-sm text-primaryLoc/90">
                        {brand.name}
                      </span>
                      <span className="text-xs text-primaryLoc/60">
                        ({brand.count})
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-primaryLoc/10 bg-white p-4">
                <h3 className="mb-4 font-semibold text-primaryLoc">
                  {ui.priceLabel}
                </h3>
                <div className="px-2">
                  <div className="relative h-2 pt-6">
                    <div className="absolute top-6 h-2 w-full rounded-lg bg-primaryLoc/10" />
                    <div
                      className="absolute top-6 h-2 rounded-lg bg-primaryLoc"
                      style={{
                        left: `${((priceRange[0] - PRICE_MIN) / PRICE_SPAN) * 100}%`,
                        width: `${((priceRange[1] - priceRange[0]) / PRICE_SPAN) * 100}%`,
                      }}
                    />
                    <input
                      title="Prix minimum"
                      type="range"
                      min={PRICE_MIN}
                      max={PRICE_MAX}
                      step="50"
                      value={priceRange[0]}
                      onChange={(e) =>
                        handlePriceChange(Number(e.target.value), true)
                      }
                      className="pointer-events-none absolute top-6 z-20 h-2 w-full cursor-pointer appearance-none bg-transparent accent-primaryLoc [&::-webkit-slider-thumb]:pointer-events-auto"
                      style={{ zIndex: 3 }}
                    />
                    <input
                      title="Prix maximum"
                      type="range"
                      min={PRICE_MIN}
                      max={PRICE_MAX}
                      step="50"
                      value={priceRange[1]}
                      onChange={(e) =>
                        handlePriceChange(Number(e.target.value), false)
                      }
                      className="pointer-events-none absolute top-6 z-20 h-2 w-full cursor-pointer appearance-none bg-transparent accent-primaryLoc [&::-webkit-slider-thumb]:pointer-events-auto"
                      style={{ zIndex: 4 }}
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-between text-sm text-primaryLoc/70">
                    <span>{priceRange[0].toLocaleString()}€</span>
                    <span>{priceRange[1].toLocaleString()}€</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="sticky top-[90px] z-20 mb-4 rounded-2xl border border-primaryLoc/15 bg-white/95 p-2.5 shadow-sm backdrop-blur sm:p-3">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                <label className="relative w-full">
                  <Icon
                    icon="material-symbols:search"
                    className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primaryLoc/60"
                  />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={ui.searchPlaceholder}
                    className="h-11 w-full rounded-xl border border-primaryLoc/25 bg-white py-3 pe-4 ps-12 text-sm text-primaryLoc outline-none transition placeholder:text-primaryLoc/45 focus:border-primaryLoc focus:ring-2 focus:ring-primaryLoc/15 sm:h-12"
                  />
                </label>

                <div className="w-full sm:w-auto">
                  <div className="relative w-full sm:w-auto">
                    <button
                      onClick={() => setIsSortOpen(!isSortOpen)}
                      className="inline-flex h-11 w-full max-w-full items-center justify-between gap-2 rounded-xl border border-primaryLoc/25 px-4 text-sm text-primaryLoc transition-colors hover:border-primaryLoc sm:h-12 sm:w-auto"
                    >
                      <span className="hidden sm:inline">{ui.sortedBy}</span>
                      <span className="max-w-[9.5rem] truncate sm:max-w-none">
                        {SORT_OPTIONS[sortBy]}
                      </span>
                      <Icon icon="mdi:chevron-down" className="h-5 w-5" />
                    </button>

                    {isSortOpen && (
                      <div className="absolute right-0 top-full z-10 mt-1 w-56 rounded-lg border border-primaryLoc/20 bg-white py-1 shadow-lg">
                        {(
                          Object.entries(SORT_OPTIONS) as [SortOption, string][]
                        ).map(([value, label]) => (
                          <button
                            key={value}
                            onClick={() => {
                              setSortBy(value);
                              setIsSortOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-start text-sm transition-colors hover:bg-secondary ${
                              sortBy === value
                                ? "bg-secondary text-primaryLoc"
                                : "text-primaryLoc/80"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 hidden flex-wrap items-center justify-between gap-3 border-t border-primaryLoc/10 pt-3 md:flex">
                <div className="flex items-center gap-2 rounded-xl border border-primaryLoc/20 bg-white p-1">
                  <button
                    onClick={() => setGridSize(2)}
                    className={`rounded-lg p-2 transition-colors ${
                      gridSize === 2
                        ? "bg-primaryLoc text-white"
                        : "text-primaryLoc/75 hover:bg-secondary"
                    }`}
                    title={ui.view2}
                    aria-label={ui.view2}
                  >
                    <Icon icon="mdi:view-grid-outline" className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setGridSize(3)}
                    className={`rounded-lg p-2 transition-colors ${
                      gridSize === 3
                        ? "bg-primaryLoc text-white"
                        : "text-primaryLoc/75 hover:bg-secondary"
                    }`}
                    title={ui.view3}
                    aria-label={ui.view3}
                  >
                    <Icon icon="mdi:view-grid" className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm font-medium text-primaryLoc">
                  {ui.available(filteredVehicles.length)}
                </p>
              </div>
            </div>

            <div
              className={`grid gap-4 sm:gap-6 ${
                gridSize === 3
                  ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
            >
              {sortedVehicles.length > 0 ? (
                sortedVehicles.map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    locale={locale}
                  />
                ))
              ) : (
                <div className="col-span-full rounded-xl border border-primaryLoc/15 bg-secondary p-6 text-start">
                  <h3 className="text-sm font-semibold text-primaryLoc">
                    {ui.emptyTitle}
                  </h3>
                  <p className="mt-1 text-sm text-primaryLoc/80">
                    {ui.emptyText}
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setBrands(INITIAL_BRANDS);
                      setPriceRange([PRICE_MIN, PRICE_MAX]);
                    }}
                    className="mt-3 rounded-lg border border-primaryLoc/25 px-4 py-2 text-sm text-primaryLoc transition hover:border-primaryLoc"
                  >
                    {ui.reset}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="fixed bottom-4 left-0 z-20 w-full px-6 lg:hidden">
          <div className="mx-auto grid max-w-[90rem] grid-cols-2 gap-2 rounded-xl border border-primaryLoc/20 bg-white/95 p-2 shadow-lg backdrop-blur">
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-primaryLoc/20 text-sm font-medium text-primaryLoc"
            >
              <Icon icon="material-symbols:filter-list" className="h-5 w-5" />
              {ui.filters}
            </button>
            <Link
              href={`#${LOCATION_BOOKING_FORM_ID}`}
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-primaryLoc text-sm font-semibold text-white"
            >
              {ui.bookThis}
            </Link>
          </div>
        </div>

        {isFilterOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            onClick={() => setIsFilterOpen(false)}
          />
        )}
      </div>
    </section>
  );
}
